<template>
  <div class="row-span-2 md:row-span-1">
    <NuxtLink :to="props.item.url" :title="props.item.title">
      <OnImg :src="props.item.image" :title="props.item.title" :alt="props.item.title" width="140" height="105" class="w-[104px] overflow-hidden rounded md:w-auto" />
    </NuxtLink>
  </div>
  <div>
    <div class="flex flex-col">
      <div class="mb-2 md:flex">
        <NuxtLink class="text-button-l font-medium md:text-button-m" :to="props.item.url" :title="props.item.title">
          {{ props.item.name }} <span class="block whitespace-nowrap text-sm text-text-light md:inline">{{ props.item.productCode }}</span>
        </NuxtLink>
      </div>
      <ProductAdditionalInfo :item="props.item" class="text-text-medium" />
    </div>
    <div class="mt-4 flex flex-col gap-2 md:gap-1">
      <div class="flex items-baseline justify-between text-sm md:block">
        <span class="truncate">{{ t['w.cart.table.regular_price_col']() }}: </span>
        <span class="text-base">
          <Price
            :large-fractional="props.item.discountPrice !== props.item.regularPrice"
            :class="{
              'text-sm text-text-light line-through md:text-base': props.item.discountPrice !== props.item.regularPrice,
            }"
            :amount="props.item.regularPrice"
          />
          <template v-if="props.item.discountPrice !== props.item.regularPrice">&nbsp;<Price :amount="props.item.discountPrice" /></template>
        </span>
      </div>

      <div
        v-if="props.item.discountPrice && props.item.bestCustomerPrice !== props.item.discountPrice && props.item.bestCustomerPrice !== props.item.regularPrice"
        class="flex items-baseline justify-between text-sm md:block"
      >
        <span class="truncate">{{ t['w.cart.table.client_price_col']() }}: </span>
        <Price class="text-base text-text-strong" :amount="props.item.bestCustomerPrice" />
      </div>
    </div>
  </div>
  <div class="mt-4 flex grow items-center justify-end md:mt-0">
    <WishlistButton
      :id="item.productId"
      class="btn-flat mr-6 md:mr-1"
      :option-ids="props.item.optionIds"
      :is-added="wishlisted"
      @on-added-to-wishlist="addToWishlist"
      @on-removed-from-wishlist="wishlisted = false"
    />
    <Button type="button" class="btn-icon btn-rounded btn-flat mr-6 md:mr-1" icon-name="trash" @click="emit('removeProduct', props.item)" />
    <PurchaseAmountDropdown v-model="computedPurchaseAmount" :open-on-hover="false" :max="item.maxQuantity" />
  </div>
  <div class="col-span-2 my-6 border-b border-border-medium md:col-span-3" />
</template>

<script setup lang="ts">
import Price from '~/components/price/Price.vue'
import * as t from '~/messages'
import type { IBasketItem } from '~/components/order/BasketTypes'
import PurchaseAmountDropdown from '~/components/product/PurchaseAmountDropdown.vue'
import WishlistButton from '~/components/wishlist-button/WishlistButton.vue'
import ProductAdditionalInfo from '~/components/order/ProductAdditionalInfo.vue'

const { proxy: gtmProxy } = useGtm()

const props = defineProps<{
  item: IBasketItem
}>()

const wishlisted = ref(props.item.wishlisted)

const computedPurchaseAmount = computed({
  get() {
    return props.item.quantity
  },
  set(newValue) {
    setQuantity(newValue)
  },
})

const addToWishlist = () => {
  wishlisted.value = true
  sendGtmAddToWishlistEvent()
}

const sendGtmAddToWishlistEvent = () => {
  gtmProxy.dataLayer.push({
    event: 'addToWishlist',
    ecommerce: {
      currencyCode: useCurrencyCode(),
      add: {
        products: [
          {
            name: props.item.name,
            id: props.item.productId,
            price: props.item.bestCustomerPrice ? props.item.bestCustomerPrice : props.item.discountPrice ? props.item.discountPrice : props.item.regularPrice,
          },
        ],
      },
    },
  })
}

const setQuantity = (newQuantity: number) => {
  const itemWithNewQuantity: IBasketItem = {
    ...props.item,
    quantity: newQuantity,
  }

  emit('updateQuantity', itemWithNewQuantity)
}

const emit = defineEmits<{
  removeProduct: [item: IBasketItem]
  updateQuantity: [item: IBasketItem]
}>()
</script>
